import { RefCallback } from "react"
import { createPortal } from "react-dom"

import ToastNotification from "../ToastNotification/ToastNotification"
import {
  DismissAllBar,
  DismissAllButton,
  ToastItem,
  ToastViewport
} from "./styled"
import type { ToastInternal, ToastPlacement, ToneColorOverrides } from "./types"

const DISMISS_ALL_THRESHOLD = 2

type RenderToastItemParams = {
  toast: ToastInternal
  index: number
  total: number
  prefersReducedMotion: boolean
  staggerMs: number
  defaultAutoDismissDelay: number
  getDismissHandler: (id: string) => () => void
  registerToastRef: (id: string) => RefCallback<HTMLDivElement>
  solidBackground: boolean
  toneColors?: ToneColorOverrides
}

const renderToastItem = ({
  toast,
  index,
  total,
  prefersReducedMotion,
  staggerMs,
  defaultAutoDismissDelay,
  getDismissHandler,
  registerToastRef,
  solidBackground,
  toneColors
}: RenderToastItemParams) => {
  const isExiting = toast.status === "exiting"
  const staggerDelay = prefersReducedMotion
    ? undefined
    : `${(total - index - 1) * staggerMs}ms`
  const allowAutoDismiss = toast.options.autoDismiss
  const animationStyle = staggerDelay
    ? { animationDelay: staggerDelay }
    : undefined
  const transitionStyle = staggerDelay
    ? { transitionDelay: staggerDelay }
    : undefined

  const resolvedSolidBackground =
    toast.options.solidBackground ?? solidBackground ?? false
  const resolvedToneColors = toast.options.toneColors
    ? { ...(toneColors ?? {}), ...toast.options.toneColors }
    : toneColors
  const handleDelivered = toast.options.onDelivered

  return (
    <ToastItem $isExiting={isExiting} key={toast.id} style={transitionStyle}>
      <ToastNotification
        actions={toast.options.actions}
        ariaLive={toast.options.ariaLive}
        autoDismiss={allowAutoDismiss}
        autoDismissDelay={
          toast.options.autoDismissDelay ?? defaultAutoDismissDelay
        }
        dismissLabel={toast.options.dismissLabel}
        message={toast.options.message}
        onDelivered={handleDelivered}
        onDismiss={getDismissHandler(toast.id)}
        reducedMotion={prefersReducedMotion}
        ref={registerToastRef(toast.id)}
        solidBackground={resolvedSolidBackground}
        style={animationStyle}
        title={toast.options.title}
        tone={toast.options.tone}
        toneColors={resolvedToneColors}
        visible={!isExiting}
      />
    </ToastItem>
  )
}

type RenderToastLayerParams = {
  visibleToasts: ToastInternal[]
  gap: number
  prefersReducedMotion: boolean
  defaultAutoDismissDelay: number
  staggerMs: number
  dismissAll: () => void
  registerToastRef: (id: string) => RefCallback<HTMLDivElement>
  getDismissHandler: (id: string) => () => void
  desktopPlacement: ToastPlacement
  tabletPlacement: ToastPlacement
  mobilePlacement: ToastPlacement
  solidBackground: boolean
  toneColors?: ToneColorOverrides
}

export const renderToastLayer = ({
  visibleToasts,
  gap,
  prefersReducedMotion,
  defaultAutoDismissDelay,
  staggerMs,
  dismissAll,
  registerToastRef,
  getDismissHandler,
  desktopPlacement,
  tabletPlacement,
  mobilePlacement,
  solidBackground,
  toneColors
}: RenderToastLayerParams) => {
  if (typeof document === "undefined") {
    return null
  }

  const displayToasts = [...visibleToasts].reverse()
  const showDismissAll =
    visibleToasts.filter(
      (toast) =>
        toast.status === "visible" && toast.options.autoDismiss !== false
    ).length > DISMISS_ALL_THRESHOLD

  return createPortal(
    <ToastViewport
      $desktopPlacement={desktopPlacement}
      $gap={gap}
      $mobilePlacement={mobilePlacement}
      $tabletPlacement={tabletPlacement}
      aria-label="Notification stack"
      data-placement-desktop={desktopPlacement}
      data-placement-mobile={mobilePlacement}
      data-placement-tablet={tabletPlacement}
      role="region"
    >
      {showDismissAll ? (
        <DismissAllBar>
          <DismissAllButton
            label="Dismiss all"
            onClick={dismissAll}
            secondary
            small
            type="button"
          />
        </DismissAllBar>
      ) : null}

      {displayToasts.map((toast, index) =>
        renderToastItem({
          toast,
          index,
          total: displayToasts.length,
          prefersReducedMotion,
          staggerMs,
          defaultAutoDismissDelay,
          getDismissHandler,
          registerToastRef,
          solidBackground,
          toneColors
        })
      )}
    </ToastViewport>,
    document.body
  )
}
